/**
 * IndustryFreshnessBadge — small amber chip that surfaces when the org's
 * underlying industry profile has been mutated in the last N days by
 * an admin OTHER than the current user.
 *
 * Why this matters: org admins running an ROI play deserve to know the
 * coefficient bundle their result was calculated against just shifted —
 * otherwise quietly-updated baselines / weights / elasticities show up
 * as inexplicable swings in their numbers.
 *
 * Renders nothing when:
 *   - org has no industry tagged (no profile = no change to warn about)
 *   - no qualifying activity in the window
 *   - the caller themselves is the actor (they already know)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authedFetch from '../../lib/authedFetch';
import { Clock, ExternalLink } from 'lucide-react';
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from '../ui/hover-card';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_PHRASE = {
  updated: 'edited',
  archived: 'archived',
  hard_deleted: 'deleted',
};

function relativeWindow(days_ago, hours_ago) {
  if (hours_ago != null && hours_ago < 24) {
    if (hours_ago < 1) return 'just now';
    return `${hours_ago}h ago`;
  }
  if (days_ago == null) return 'recently';
  if (days_ago === 0) return 'today';
  if (days_ago === 1) return 'yesterday';
  return `${days_ago}d ago`;
}

export default function IndustryFreshnessBadge({
  orgId,
  days = 7,
  variant = 'inline',
}) {
  const [change, setChange] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    if (!orgId) { setLoading(false); return undefined; }
    (async () => {
      try {
        const res = await authedFetch(
          `${API}/api/organizations/${orgId}/roi-profile/freshness?days=${days}`,
        );
        if (!res.ok) {
          if (!cancelled) setChange(null);
          return;
        }
        const data = await res.json();
        if (!cancelled) setChange(data?.change || null);
      } catch {
        if (!cancelled) setChange(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, days]);

  if (loading || !change) return null;

  const verb = ACTION_PHRASE[change.action] || 'changed';
  const when = relativeWindow(change.days_ago, change.hours_ago);
  const actor = change.actor_email
    ? String(change.actor_email).split('@')[0]
    : 'another admin';
  const industryName = change.industry_name || change.industry_id || 'this industry';

  // Compact pill — the rest lives in the hover popover so the page
  // chrome doesn't grow on every ROI screen.
  const sharedTrigger = (
    <button
      type="button"
      onClick={() => navigate('/admin/coefficient-history')}
      data-testid="industry-freshness-badge"
      data-action={change.action}
      aria-label={`Industry coefficients ${verb} ${when} by ${actor}. Click to review.`}
      className={`inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 transition-colors ${variant === 'block' ? 'w-fit' : ''}`}
    >
      <Clock className="h-3 w-3 text-amber-600" />
      Coefficients {verb} {when}
    </button>
  );

  return (
    <HoverCard openDelay={120} closeDelay={120}>
      <HoverCardTrigger asChild>
        {sharedTrigger}
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-80 p-0 shadow-xl border-amber-100"
        data-testid="industry-freshness-popover"
      >
        <div className="px-4 py-3 border-b bg-gradient-to-r from-amber-50 to-white">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">
            Heads-up · coefficients shifted
          </div>
          <div className="text-sm font-bold text-slate-900 mt-0.5">
            {industryName} was {verb} {when}
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5">
            by <span className="font-mono">{actor}</span>
          </div>
        </div>
        {change.summary && (
          <div className="px-4 py-2.5 text-xs text-slate-700 leading-relaxed border-b">
            {change.summary}
          </div>
        )}
        {Array.isArray(change.diff) && change.diff.length > 0 && (
          <ul className="px-4 py-2 text-[11px] space-y-1 border-b" data-testid="industry-freshness-diff">
            {change.diff.slice(0, 5).map((d, i) => (
              <li key={i} className="flex items-baseline gap-2 font-mono">
                <span className="text-slate-500 shrink-0">{d.field}</span>
                <span className="text-rose-600 truncate">
                  {d.kind === 'count' ? `${d.from} rows` : String(d.from ?? '∅')}
                </span>
                <span className="text-slate-400">→</span>
                <span className="text-emerald-700 truncate">
                  {d.kind === 'count' ? `${d.to} rows` : String(d.to ?? '∅')}
                </span>
              </li>
            ))}
            {change.diff.length > 5 && (
              <li className="text-[10px] text-slate-400">
                + {change.diff.length - 5} more field{change.diff.length - 5 === 1 ? '' : 's'}
              </li>
            )}
          </ul>
        )}
        <div className="px-4 py-2 flex items-center justify-between bg-slate-50">
          <div className="text-[10px] text-slate-500 leading-tight">
            Your ROI numbers reflect the latest coefficients.
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/coefficient-history')}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-900"
            data-testid="industry-freshness-review"
          >
            Review <ExternalLink className="h-2.5 w-2.5" />
          </button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
